// Rejestracja Service Workera + kontrolowana aktualizacja PWA.
//
// Service Workera generuje teraz vite-plugin-pwa (Workbox) — nie ma już ręcznego
// public/sw.js. Tutaj tylko podpinamy się pod jego cykl życia przez
// `virtual:pwa-register`.
//
// Mechanizm:
// 1. registerType: 'prompt' (vite.config.ts) => gdy Workbox wykryje nową wersję,
//    wywołuje onNeedRefresh() — SAM z siebie NIE przeładowuje strony.
// 2. Reasumpcję (skipWaiting + reload) odpalamy dopiero przez updateSW() — i to
//    NIGDY w trakcie aktywnego dyktowania/przetwarzania notatki (patrz
//    setDictationActive) — w takim wypadku reload jest odkładany (reloadWhenSafe).
// 3. Dodatkowe sprawdzenie aktualizacji: przy powrocie na pierwszy plan
//    (visibilitychange) oraz przy powrocie połączenia (checkForUpdate wołane
//    z useNetworkSync) — celowo BEZ cyklicznego pollingu w tle.

import { registerSW } from 'virtual:pwa-register'

let registration: ServiceWorkerRegistration | null = null
// Funkcja zwracana przez registerSW — updateSW(true) => skipWaiting + reload.
let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null

let dictationActive = false
let reloadPending = false

const RELOAD_RETRY_MS = 2000
const RELOAD_MAX_RETRIES = 30 // ok. 1 minuta odkładania, potem czeka do następnej okazji

// Słuchacze końca dyktowania — używane przez useNetworkSync do wyzwolenia
// synchronizacji natychmiast po zakończeniu dyktowania (patrz isDictationActive
// niżej: synchronizacja nigdy nie startuje W TRAKCIE dyktowania, więc bez tego
// musiałaby czekać na następny cykl/zdarzenie 'online').
const dictationEndListeners: (() => void)[] = []
export function onDictationEnd(cb: () => void): void {
  dictationEndListeners.push(cb)
}

export function isDictationActive(): boolean {
  return dictationActive
}

export function setDictationActive(active: boolean): void {
  dictationActive = active
  if (!active) {
    if (reloadPending) {
      reloadPending = false
      applyUpdate()
      return
    }
    dictationEndListeners.forEach(cb => { try { cb() } catch (_) {} })
  }
}

// Zastosuj oczekującą aktualizację Service Workera. updateSW(true) wysyła
// skipWaiting do nowego SW; po przejęciu kontroli virtual:pwa-register sam
// przeładowuje stronę. Fallback do location.reload(), gdy SW niedostępny.
function applyUpdate(): void {
  if (updateSW) {
    void updateSW(true)
  } else {
    window.location.reload()
  }
}

function reloadWhenSafe(retries = RELOAD_MAX_RETRIES): void {
  if (!dictationActive) {
    applyUpdate()
    return
  }
  reloadPending = true
  if (retries <= 0) return // spróbujemy ponownie po zakończeniu dyktowania (setDictationActive(false))
  setTimeout(() => reloadWhenSafe(retries - 1), RELOAD_RETRY_MS)
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return

  updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      // Nowa wersja czeka w stanie "waiting". Odkładamy reasumpcję, jeśli trwa
      // dyktowanie — inaczej stosujemy od razu (stara logika: reloadWhenSafe).
      console.log('[PWA] Wykryto nową wersję — zaplanowano aktualizację.')
      reloadWhenSafe()
    },
    onOfflineReady() {
      console.log('[PWA] Aplikacja gotowa do pracy offline.')
    },
    onRegisteredSW(_swScriptUrl, reg) {
      registration = reg ?? null
    },
    onRegisterError(err) {
      console.warn('[SW] Błąd rejestracji service workera:', err)
    },
  })

  // Telefon/PWA bardzo często NIE robi pełnego przeładowania po "otwarciu aplikacji" —
  // system tylko wznawia zawieszoną w tle kartę (bez ponownego wykonania JS-a).
  // Dlatego dodatkowo sprawdzamy aktualizację przy każdym powrocie na pierwszy plan
  // (zdarzeniowo, NIE cyklicznie w tle).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      checkForUpdate()
    }
  })
}

// Wywoływane z useNetworkSync przy powrocie połączenia — bez cyklicznego pollingu.
export function checkForUpdate(): void {
  registration?.update().catch(() => {})
}
